/* MyBoost product page — ES module. Pure helpers are exported for node tests; the browser part
   enhances every [data-mba-product] rendered by sections/mb-product-main.liquid.
   Dependencies (mb-account-check.js, mb-shell.js) are loaded by URL from data attributes (I-3). */

export function format(template, values = {}) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (whole, name) => (name in values ? String(values[name]) : whole));
}

// "10 k" -> 10000, "1 M" -> 1000000, "2 500" -> 2500 (labels printed by snippets/mb-variant-label.liquid).
export function labelToNumber(label) {
  const match = String(label ?? '').replace(/\s+/g, '').match(/^(\d+(?:[.,]\d+)?)([kKmM]?)$/);
  if (!match) return 0;
  const factor = { k: 1000, m: 1000000 }[match[2].toLowerCase()] ?? 1;
  return Math.round(Number(match[1].replace(',', '.')) * factor);
}

// Price of one delivered unit (pack + bonus), rounded to the nearest cent — never down, which would
// advertise less than the customer pays — and quoted per 100, then per 1 000, once a unit falls
// under two cents. Same integer steps as mb-product-buy.liquid, so both lines always agree.
export function unitPriceParts(priceCents, total) {
  const price = Number(priceCents);
  const units = Number(total);
  if (!Number.isFinite(price) || !Number.isFinite(units) || units <= 0 || price <= 0) return null;
  let last = null;
  for (const step of [1, 100, 1000]) {
    const cents = Math.round((price * step) / units);
    last = { cents, step };
    if (cents >= 2) return last;
  }
  return last.cents >= 1 ? last : null;
}

export function summaryText(variant, unit, bonusTemplate) {
  if (!variant) return '';
  const base = [variant.qty, unit].filter(Boolean).join(' ');
  return variant.bonus ? `${base} ${format(bonusTemplate, { count: variant.bonus }).replace('+', '+ ')}` : base;
}

// What the order delivers in total: "3 publications · 3 000 vues" rather than "3 × 1 000 vues",
// which left the customer multiplying on the one line that has to be unambiguous.
export function orderSummary({ variant, lines, unit, bonusTemplate, timesTemplate, postsUnit, number }) {
  if (!variant) return '';
  if (lines <= 1 || !timesTemplate) return summaryText(variant, unit, bonusTemplate);
  const scale = (label) => (label ? number(labelToNumber(label) * lines) : '');
  const totals = { qty: scale(variant.qty), bonus: scale(variant.bonus) };
  return format(timesTemplate, {
    count: number(lines),
    unit: postsUnit,
    summary: summaryText(totals, unit, bonusTemplate),
  });
}

// values: [{ bonus, available }] -> index of the default pack (same rule as mb-product-main.liquid):
// every pack unavailable -> the first one (deterministic, shown « Indisponible »); -1 only when there is none.
export function pickDefaultQuantity(values) {
  const available = [];
  (values ?? []).forEach((value, index) => {
    if (value && value.available) available.push({ index, bonus: value.bonus });
  });
  const withBonus = available.find((value) => value.bonus);
  if (withBonus) return withBonus.index;
  if (available.length > 1) return available[1].index;
  if (available.length) return available[0].index;
  return values?.length ? 0 : -1;
}

// One variant per pack (quantity value index q), chosen by sections/mb-product-main.liquid.
export function findVariant(variants, { q } = {}) {
  return (variants ?? []).find((variant) => variant.q === q) ?? null;
}

// An unavailable pack blocks buying first, whatever the account/link state.
export function buyState({ api, target, checkStatus, needPost, postChosen, linkValid, available }) {
  if (available === false) return { enabled: false, reasonKey: 'packs.unavailable' };
  if (!api) return linkValid ? { enabled: true, reasonKey: null } : { enabled: false, reasonKey: 'buy.need_account' };
  if (checkStatus === 'private') return { enabled: false, reasonKey: 'buy.private' };
  if (checkStatus !== 'public' && checkStatus !== 'unavailable') return { enabled: false, reasonKey: 'buy.need_account' };
  if ((needPost ?? target === 'post') && !postChosen) return { enabled: false, reasonKey: 'buy.need_post' };
  return { enabled: true, reasonKey: null };
}

// API post products: the post step is always shown, locked until the account check allows it.
// public -> unlocked and posts loaded; unavailable -> unlocked for the paste-link field only; else locked.
export function postStepMode(checkStatus) {
  if (checkStatus === 'public') return { locked: false, load: true };
  if (checkStatus === 'unavailable') return { locked: false, load: false };
  return { locked: true, load: false };
}

// gain = share of the projected total that the order adds, in % (min 4 so the bar stays visible).
export function projection(followers, qty, bonus) {
  if (followers === null || followers === undefined || followers === '') return null;
  const from = Number(followers);
  if (!Number.isFinite(from) || from < 0) return null;
  const added = Math.max(0, Number(qty) || 0) + Math.max(0, Number(bonus) || 0);
  const to = from + added;
  if (!added) return { from, to, gain: 0 };
  return { from, to, gain: projectionFill(added / Math.max(from, 1)) };
}

// How full the projection bar reads. A linear share (added / total) is honest but unreadable: 1 000
// followers on a 30 000 account is 3 % of a bar, which looks like the pack does nothing. The numbers
// either side of the bar stay exact — this only controls the fill — so it starts at a quarter for any
// real gain and reaches the end when the account doubles.
export const PROJECTION_FLOOR = 25;
export function projectionFill(ratio) {
  const growth = Number(ratio);
  if (!Number.isFinite(growth) || growth <= 0) return 0;
  const curved = Math.log(1 + Math.min(growth, 1)) / Math.LN2;
  return Math.min(100, Math.round(PROJECTION_FLOOR + (100 - PROJECTION_FLOOR) * curved));
}

export function debounce(fn, wait) {
  let timer = null;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
  debounced.cancel = () => {
    clearTimeout(timer);
    timer = null;
  };
  return debounced;
}

export function checkoutUrl(code) {
  const value = String(code ?? '').trim();
  return value ? `/discount/${encodeURIComponent(value)}?redirect=/checkout` : '/checkout';
}

// I2: TgdShim binds its checkout targets at load (#checkout, button[name=checkout], the /cart submit),
// so an element made afterwards is never intercepted: buy now clicks one the shim already holds.
export function checkoutTarget(doc) {
  const at = (selector) => doc?.querySelector?.(selector) ?? null;
  return at('#checkout') || at('button[name="checkout"]') || at('form[action$="/cart"] button[type="submit"]');
}

export function nearestSlide(offsets, scrollLeft) {
  const position = Math.abs(Number(scrollLeft) || 0);
  let best = 0;
  (offsets ?? []).forEach((offset, index) => {
    if (Math.abs(offset - position) < Math.abs(offsets[best] - position)) best = index;
  });
  return best;
}

// Relay images only: https URLs (absolute or root-relative) whose origin equals the relay's own origin.
export function resolveMediaUrl(url, relayBase) {
  const raw = url === null || url === undefined ? '' : String(url);
  if (!raw) return '';
  let base;
  try {
    base = new URL(String(relayBase ?? ''));
  } catch {
    return '';
  }
  if (base.protocol !== 'https:') return '';
  let resolved;
  try {
    resolved = new URL(raw, base);
  } catch {
    return '';
  }
  return resolved.protocol === 'https:' && resolved.origin === base.origin ? resolved.href : '';
}

// I3: only modules exposing the expected shape are used; a malformed or missing dependency keeps the
// browser part's local fallbacks instead of throwing inside the loader (so `ready` never rejects).
export function loadShellModule(module) {
  return module && typeof module.validateTarget === 'function' && typeof module.formatPrice === 'function' ? module : null;
}

export function loadAccountCheckModule(module) {
  return module
    && typeof module.createChecker === 'function'
    && typeof module.parseHandle === 'function'
    && typeof module.isValidHandle === 'function'
    && typeof module.formatCount === 'function'
    && typeof module.lineProperties === 'function'
    ? module : null;
}

// I4: pure cart line item builder (I-4). Mirrors mb-account-check.js's lineProperties() shape exactly so the
// inline fallback (module A failed to load) and the real module never disagree on what reaches the cart.
export function buildLineItem({ api, target, status, handle, post, link, variant, sellingPlanId, checks, keys, platform }) {
  if (!variant) return null;
  let value = link;
  let check = 'format';
  let account = null;
  if (api) {
    check = status === 'public' ? 'verified' : 'unverified';
    value = target === 'post' ? post : handle;
    account = target === 'post' ? handle : null;
  }
  const properties = checks && typeof checks.lineProperties === 'function'
    ? checks.lineProperties({ targetType: target, value, check, platform, account, keys })
    : { [target === 'post' ? keys.post : keys.profile]: value, _mb_check: check, _mb_platform: platform, ...(account ? { _mb_account: account } : {}) };
  const item = { id: variant.id, quantity: 1, properties };
  if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
  return item;
}

// One cart line per chosen post, because each line carries its own link and is delivered separately —
// a quantity of 3 on a single line would tell fulfilment "three times this one video". A profile
// product, or a product whose target is pasted as a link, is always exactly one line.
export function buildLineItems({ posts, ...rest }) {
  const targets = rest.api && rest.target === 'post' ? (posts ?? []) : [];
  if (!targets.length) {
    // Nothing ticked yet: still one line, with an empty target rather than undefined. The buy button
    // is disabled in that state, but this shape is what the express form reads.
    const single = buildLineItem({ post: '', ...rest });
    return single ? [single] : [];
  }
  return targets.map((post) => buildLineItem({ ...rest, post })).filter(Boolean);
}

// C2 / I4: purchase guard — a re-check result is stale once its own request was aborted, or once the
// account field moved on to a different handle while the relay call was in flight.
export function isStaleCheck({ aborted, requestedHandle, currentHandle }) {
  return Boolean(aborted) || requestedHandle !== currentHandle;
}

// I1: Shopify's t filter HTML-escapes translated strings; the data-mba-product-data JSON block is script
// *text content*, not an HTML attribute, so the browser never decodes those entities on its own. Decode
// once, here, before the strings are used. Identity outside the browser (Node tests: no document).
export function decodeEntities(value) {
  const raw = value === null || value === undefined ? '' : String(value);
  if (!raw || typeof document === 'undefined') return raw;
  const box = document.createElement('textarea');
  box.innerHTML = raw;
  return box.value;
}

// Submit-handler routing: the pressed button decides, not document.activeElement. Safari/iOS never
// moves focus to a tapped submit button, so a customer who types their account and taps « Ajouter au
// panier » must still add to cart, not re-run the account check that a focus-based branch would trigger.
// event.submitter is only set for a real button/input submit (never for e.g. Enter with no submitter),
// so the activeElement fallback below still covers that case.
export function submitAction({ submitter, active, fields = {} }) {
  if (submitter?.matches?.('[data-mba-buy-now]')) return 'buy-now';
  if (submitter?.matches?.('[data-mba-add]')) return 'add';
  if (active === fields.account) return 'check-account';
  if (active === fields.link) return 'check-link';
  if (active === fields.postLink) return 'choose-post';
  return 'add';
}

const STATES = ['public', 'private', 'not_found', 'invalid', 'unavailable'];
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const readDiscountCode = () => {
  try {
    return window.localStorage.getItem('mb_discount_code') || '';
  } catch {
    return '';
  }
};

// The cart bridge pushes the line properties on the clicked element; with no target on the page the
// shim is not there either, so going to the checkout URL is the whole job.
async function clickCheckoutLink(code) {
  if (typeof document === 'undefined') return;
  // Tagada redirige en interceptant un clic sur le bouton de paiement, mais seulement si ce bouton
  // est reellement affiche : celui du tiroir reste inerte tant que le tiroir est ferme, et le clic
  // partait dans le vide alors que l'article venait d'etre ajoute. On ouvre donc le tiroir, on lui
  // laisse le temps de se peindre, puis on clique. Le client ne quitte jamais sa fiche produit.
  const drawer = document.getElementById('MinimogCartDrawer');
  if (drawer) {
    try {
      if (typeof drawer.onCartDrawerUpdate === 'function') await drawer.onCartDrawerUpdate();
      if (typeof drawer.open === 'function') drawer.open();
    } catch {
      /* le tiroir refuse de s'ouvrir : on tente quand meme le clic ci-dessous */
    }
    await new Promise((resolve) => { setTimeout(resolve, 1200); });
  }
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const target = checkoutTarget(document);
    if (target) target.click();
    // Si la redirection part, la page est remplacee et la suite n'arrive jamais.
    await new Promise((resolve) => { setTimeout(resolve, 2000); });
  }
  // Tagada n'a pas repris la main : on rend la page au client, son panier est intact et le tiroir
  // ouvert lui montre son article avec le bouton de paiement sous les yeux.
  try {
    if (window.mbCheckoutVeil) window.mbCheckoutVeil.hide();
  } catch {
    /* le voile se referme de toute facon seul */
  }
  const box = document.querySelector('[data-mba-buy-error]');
  if (box) {
    const french = String(document.documentElement.lang || 'fr').toLowerCase().indexOf('fr') === 0;
    box.textContent = french
      ? 'Le paiement est momentanément indisponible. Ton panier est gardé, réessaie dans un instant.'
      : 'Checkout is temporarily unavailable. Your cart is saved, please try again shortly.';
  }
}

// Controls: thumbnails under the frame, arrows over its sides, finger swipe (native scrolling)
// and mouse drag. No counter (see mb-product-media.liquid).
function initMedia(root) {
  const media = root.querySelector('[data-mba-media]');
  const track = media?.querySelector('[data-mba-media-track]');
  if (!track) return;
  const slides = [...track.children];
  const thumbs = [...media.querySelectorAll('[data-mba-media-thumb]')];
  const prev = media.querySelector('[data-mba-media-prev]');
  const next = media.querySelector('[data-mba-media-next]');
  const rtl = () => getComputedStyle(track).direction === 'rtl';
  const offsets = () => slides.map((slide) => Math.abs(slide.offsetLeft - slides[0].offsetLeft));
  let index = 0;
  const show = (value) => {
    index = value;
    thumbs.forEach((thumb, i) => {
      if (i === value) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    thumbs[value]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (prev) prev.disabled = value === 0;
    if (next) next.disabled = value === slides.length - 1;
  };
  const go = (value) => {
    const target = Math.max(0, Math.min(slides.length - 1, value));
    track.scrollTo({ left: offsets()[target] * (rtl() ? -1 : 1), behavior: reducedMotion() ? 'auto' : 'smooth' });
    show(target);
  };
  let frame = 0;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const atEnd = Math.abs(track.scrollLeft) >= track.scrollWidth - track.clientWidth - 2;
      show(atEnd ? slides.length - 1 : nearestSlide(offsets(), track.scrollLeft));
    });
  }, { passive: true });
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => go(i)));
  prev?.addEventListener('click', () => go(index - 1));
  next?.addEventListener('click', () => go(index + 1));

  // A finger scrolls the track natively; this gives the mouse the same gesture. Only the mouse is
  // intercepted, so touch scrolling and its momentum stay untouched.
  let drag = null;
  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    drag = { x: event.clientX, left: track.scrollLeft, id: event.pointerId, moved: false };
    track.classList.add('is-dragging');
  });
  track.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 4) return;
    if (!drag.moved) {
      drag.moved = true;
      track.setPointerCapture(drag.id);
    }
    track.scrollLeft = drag.left - dx;
    event.preventDefault();
  });
  const endDrag = () => {
    if (!drag) return;
    const { moved, id } = drag;
    drag = null;
    track.classList.remove('is-dragging');
    if (moved) {
      track.releasePointerCapture(id);
      go(nearestSlide(offsets(), track.scrollLeft));
    }
  };
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);
  track.addEventListener('keydown', (event) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (step) go(index + (rtl() ? -step : step));
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(slides.length - 1);
    else return;
    event.preventDefault();
  });
}

function initSticky(root, buy, sticky) {
  if (!buy || !sticky || typeof IntersectionObserver === 'undefined') return;
  const intro = root.querySelector('[data-mba-media]');
  let buyVisible = true;
  let introVisible = Boolean(intro);
  const apply = () => {
    sticky.hidden = buyVisible || introVisible;
  };
  new IntersectionObserver(([entry]) => {
    buyVisible = entry.isIntersecting;
    apply();
  }).observe(buy);
  if (intro) {
    new IntersectionObserver(([entry]) => {
      introVisible = entry.isIntersecting;
      apply();
    }).observe(intro);
  }
}

function initProduct(root) {
  if (root.dataset.mbaReady) return;
  const form = root.querySelector('[data-mba-product-form]');
  const source = root.querySelector('[data-mba-product-data]');
  if (!form || !source) return;
  let data;
  try {
    data = JSON.parse(source.textContent);
  } catch {
    return;
  }
  root.dataset.mbaReady = 'true';
  const d = root.dataset;
  // I1: decode HTML entities once (Shopify's t filter escaped them; the JSON block is not attribute-parsed).
  const S = data.strings || {};
  for (const key of Object.keys(S)) S[key] = decodeEntities(S[key]);
  const api = d.mbaApi === 'true';
  const platform = d.mbaPlatform;
  const target = d.mbaTarget === 'post' ? 'post' : 'profile';
  const relay = (d.mbaRelay || '').replace(/\/+$/, '');
  const keys = { profile: d.profileKey, post: d.postKey };
  const $ = (selector) => root.querySelector(selector);
  const el = {
    account: $('[data-mba-account-input]'),
    output: $('[data-mba-account-output]'),
    suggest: $('[data-mba-suggest]'),
    suggestList: $('[data-mba-suggest-list]'),
    suggestTemplate: $('[data-mba-suggest-template]'),
    postsStep: $('[data-mba-posts-step]'),
    postsLocked: $('[data-mba-posts-locked]'),
    postsBody: $('[data-mba-posts-body]'),
    postsGrid: $('[data-mba-posts-grid]'),
    postsCount: $('[data-mba-posts-count]'),
    postLink: $('[data-mba-post-link]'),
    postError: $('[data-mba-post-error]'),
    postTemplate: $('[data-mba-post-template]'),
    link: $('[data-mba-link-input]'),
    linkHint: $('[data-mba-link-hint]'),
    linkError: $('[data-mba-link-error]'),
    buy: $('[data-mba-buy]'),
    price: $('[data-mba-price]'),
    compare: $('[data-mba-compare]'),
    summary: $('[data-mba-summary]'),
    each: $('[data-mba-each]'),
    reason: $('[data-mba-buy-reason]'),
    error: $('[data-mba-buy-error]'),
    add: $('[data-mba-add]'),
    now: $('[data-mba-buy-now]'),
    express: $('[data-mba-express]'),
    sticky: $('[data-mba-sticky]'),
    stickySummary: $('[data-mba-sticky-summary]'),
    stickyPrice: $('[data-mba-sticky-price]'),
    stickyAction: $('[data-mba-sticky-action]'),
  };
  form.noValidate = true;

  const state = { status: null, profile: null, handle: '', posts: [], link: '', linkOk: false, busy: false, controller: null };
  let shell = null;
  let checks = null;
  let checker = null;
  const ready = Promise.all([
    import(d.mbaShellUrl).then(loadShellModule, () => null).then((module) => { shell = module; }),
    import(d.mbaAccountCheckUrl).then(loadAccountCheckModule, () => null).then((module) => {
      checks = module;
      if (api && module) {
        try {
          checker = module.createChecker({ baseUrl: relay });
        } catch {
          checker = null;
        }
      }
    }),
  ]);

  const money = (cents) => (shell ? shell.formatPrice(cents, d.locale, d.currency) : `${(Number(cents) / 100).toFixed(2)} ${d.currency || ''}`.trim());
  // Grouped, not compact: this total is what the customer checks against what they paid, so
  // "3 000 vues" and never "3 k vues".
  const plain = (n) => {
    try {
      return new Intl.NumberFormat(d.locale).format(n).replace(/[  ]/g, ' ');
    } catch {
      return String(n);
    }
  };
  const count = (n) => {
    if (n === null || n === undefined || n === '') return '';
    return checks ? checks.formatCount(n, d.locale) : String(n);
  };
  const plan = () => form.querySelector('input[name="selling_plan"]:checked')?.value || '';
  // The pack is bought once per chosen post. Nothing selected yet still prices one, so the page shows
  // a price from the start instead of zero.
  const lineCount = () => (api && target === 'post' ? Math.max(state.posts.length, 1) : 1);
  const variant = () => {
    const pack = form.querySelector('input[name="id"]:checked');
    return pack ? findVariant(data.variants, { q: Number(pack.dataset.mbaQtyIndex) }) : null;
  };

  const updateProjection = () => {
    const box = el.output?.querySelector('[data-mba-projection]');
    if (!box) return;
    const current = variant();
    const result = state.status === 'public' && d.mbaShowProjection === 'true' && current
      ? projection(state.profile?.followers, labelToNumber(current.qty), labelToNumber(current.bonus))
      : null;
    box.hidden = !result || !result.gain;
    if (box.hidden) return;
    box.querySelector('[data-mba-projection-from]').textContent = count(result.from);
    box.querySelector('[data-mba-projection-to]').textContent = count(result.to);
    box.querySelector('[data-mba-projection-fill]')?.style.setProperty('--mba-progress', `${result.gain}%`);
  };

  // Minor: disable "Chaque mois" (and fall back to "En une fois") when the selected pack has no monthly
  // allocation price at all, instead of leaving a selling plan selectable that mb-product-packs.liquid
  // rendered for the product as a whole but that this particular variant does not actually support.
  const syncRhythm = (current) => {
    const monthlyInput = form.querySelector('input[name="selling_plan"]:not([value=""])');
    if (!monthlyInput) return;
    const hasMonthly = Boolean(current) && current.monthly_price !== null && current.monthly_price !== undefined;
    monthlyInput.disabled = !hasMonthly;
    if (!hasMonthly && monthlyInput.checked) {
      monthlyInput.checked = false;
      const onceInput = form.querySelector('input[name="selling_plan"][value=""]');
      if (onceInput) onceInput.checked = true;
    }
  };

  // « soit 0,05 € par abonné »: what the customer pays per unit actually delivered (pack + bonus).
  const unitPriceText = (current, cents) => {
    if (!current) return '';
    const total = labelToNumber(current.qty) + labelToNumber(current.bonus);
    const parts = unitPriceParts(cents, total);
    if (!parts) return '';
    const amount = money(parts.cents);
    if (parts.step === 1) return format(S['buy.unit_price'], { amount, unit: d.mbaUnitOne || d.mbaUnit || '' });
    const group = parts.step === 1000 ? '1 000' : String(parts.step);
    return format(S['buy.unit_price_group'], { amount, count: group, unit: d.mbaUnit || '' });
  };

  const refreshPrice = () => {
    const current = variant();
    if (!current) return;
    syncRhythm(current);
    const monthly = Boolean(plan()) && current.monthly_price !== null && current.monthly_price !== undefined;
    const lines = lineCount();
    const cents = (monthly ? current.monthly_price : current.price) * lines;
    const text = money(cents);
    const compare = current.compare_at_price * lines > cents ? money(current.compare_at_price * lines) : '';
    const summary = orderSummary({
      variant: current, lines, unit: d.mbaUnit, bonusTemplate: S['packs.bonus'],
      timesTemplate: S['buy.times'], postsUnit: S['post.unit'], number: plain,
    });
    if (el.price) el.price.textContent = text;
    if (el.each) el.each.textContent = unitPriceText(current, monthly ? current.monthly_price : current.price);
    if (el.compare) {
      el.compare.textContent = compare;
      el.compare.hidden = !compare;
    }
    if (el.summary) el.summary.textContent = summary;
    if (el.stickySummary) el.stickySummary.textContent = summary;
    if (el.stickyPrice) el.stickyPrice.textContent = text;
    updateProjection();
  };

  // I-4: delegates to the pure buildLineItem() so this exact composition is unit-tested (profile vs post
  // keys, _mb_account only for API post products, _mb_check values, selling_plan, inline-fallback parity).
  const lineItems = () => buildLineItems({
    api, target, status: state.status, handle: state.handle, posts: state.posts, link: state.link,
    variant: variant(), sellingPlanId: plan(), checks, keys, platform,
  });

  const syncExpress = (enabled) => {
    if (!el.express) return;
    // Shopify's dynamic checkout button posts a single variant: with several posts chosen it would
    // silently buy one of them. It is hidden rather than wrong.
    const items = enabled ? lineItems() : [];
    const item = items.length === 1 ? items[0] : null;
    el.express.hidden = !item;
    if (!item) return;
    const id = el.express.querySelector('[data-mba-express-id]');
    if (id) id.value = String(item.id);
    const planInput = el.express.querySelector('[data-mba-express-plan]');
    if (planInput) {
      planInput.value = item.selling_plan ? String(item.selling_plan) : '';
      planInput.disabled = !item.selling_plan;
    }
    el.express.querySelector('[data-mba-express-properties]')?.replaceChildren(...Object.entries(item.properties).map(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      return input;
    }));
  };

  const evaluate = () => {
    const current = variant();
    const result = buyState({ api, target, checkStatus: state.status, needPost: api && target === 'post', postChosen: state.posts.length > 0, linkValid: state.linkOk, available: Boolean(current?.available) });
    const enabled = result.enabled;
    if (el.add) el.add.disabled = !enabled || state.busy;
    if (el.now) el.now.disabled = !enabled || state.busy;
    if (el.reason) el.reason.textContent = result.reasonKey ? S[result.reasonKey] || '' : '';
    if (el.stickyAction) {
      let mode = 'verify';
      if (enabled) mode = 'add';
      else if (result.reasonKey === 'packs.unavailable') mode = 'unavailable';
      else if (state.status === 'private') mode = 'recheck';
      el.stickyAction.dataset.mbaMode = mode;
      el.stickyAction.disabled = mode === 'unavailable';
      el.stickyAction.textContent = { add: S['buy.add'], recheck: S['account.recheck'], verify: S['buy.verify'], unavailable: S['packs.unavailable'] }[mode] || '';
    }
    syncExpress(enabled);
    return enabled;
  };

  const fillProfile = (node, result) => {
    const set = (selector, text) => {
      const field = node.querySelector(selector);
      if (field) field.textContent = text;
    };
    const avatar = node.querySelector('[data-mba-avatar]');
    const src = resolveMediaUrl(result.avatar, relay);
    if (avatar && src) {
      avatar.src = src;
      avatar.hidden = false;
    }
    set('[data-mba-username]', `@${result.username || state.handle}`);
    const badge = node.querySelector('[data-mba-verified]');
    if (badge) badge.hidden = !result.verified;
    for (const [selector, key, value] of [['[data-mba-followers]', 'account.followers', result.followers], ['[data-mba-posts]', 'account.posts', result.posts], ['[data-mba-likes]', 'account.likes', result.likes]]) {
      set(selector, value === null || value === undefined ? '' : format(S[key], { count: count(value) }));
    }
  };

  const renderState = (name, result) => {
    if (!el.output) return;
    // Sous le minimum de recherche aucune suggestion ne peut etre proposee : rendre un verdict
    // negatif a cet instant alarme le client au milieu de sa frappe pour un compte qu'il n'a pas
    // fini d'ecrire (taper « re » tombe sur le compte prive @re). On garde l'etat d'attente.
    const minSearch = checks?.MIN_SEARCH_LENGTH ?? 3;
    if (String(state.handle || '').length < minSearch
      && (name === 'private' || name === 'not_found' || name === 'invalid')) {
      return renderState('checking');
    }
    // Des comptes proches sont proposes juste en dessous : afficher « Aucun compte » en rouge
    // pendant qu'on en liste trois se contredit. La liste peut dater d'une saisie precedente, elle
    // ne vaut donc que si elle porte bien sur ce qui est tape maintenant.
    if (name === 'not_found'
      && suggest.items.length > 0
      && suggest.query
      && String(state.handle || '').toLowerCase().indexOf(suggest.query) === 0) {
      el.output.replaceChildren();
      el.account?.removeAttribute('aria-invalid');
      return;
    }
    const template = root.querySelector(`template[data-mba-state="${name}"]`);
    if (!template) {
      el.output.replaceChildren();
      return;
    }
    const node = template.content.cloneNode(true);
    if (name === 'public' && result) fillProfile(node, result);
    const title = node.querySelector('[data-mba-not-found-title]');
    if (title) title.textContent = format(title.textContent, { username: result?.username || state.handle });
    el.output.replaceChildren(node);
    el.account?.setAttribute('aria-invalid', String(name === 'invalid' || name === 'not_found'));
  };

  let postsController = null;
  const lockPosts = (locked) => {
    if (!el.postsStep) return;
    el.postsStep.disabled = locked;
    if (el.postsLocked) el.postsLocked.hidden = !locked;
    if (el.postsBody) el.postsBody.hidden = locked;
  };
  // Returns the post step to its disabled hint state (never hides it: step numbers stay 1-2-3-4).
  const hidePosts = () => {
    postsController?.abort();
    state.posts = [];
    showPostCount();
    if (!el.postsStep) return;
    lockPosts(true);
    el.postsGrid?.replaceChildren();
    el.postsGrid?.removeAttribute('aria-busy');
    if (el.postLink) {
      el.postLink.value = '';
      el.postLink.removeAttribute('aria-invalid');
    }
    if (el.postError) el.postError.textContent = '';
  };

  const postTile = (item, index, total) => {
    if (!el.postTemplate || !/^https:\/\//i.test(item?.url || '')) return null;
    const tile = el.postTemplate.content.cloneNode(true).firstElementChild;
    const box = tile.querySelector('[data-mba-post-radio]');
    // No name on purpose: these are checkboxes inside the product form, and a named one would post
    // every ticked value to /cart/add alongside the lines the script builds itself.
    box.value = item.url;
    box.setAttribute('aria-label', format(S['media.count'], { current: index + 1, total }));
    const image = tile.querySelector('img');
    const src = resolveMediaUrl(item.thumbnail, relay);
    if (src) image.src = src;
    else image.remove();
    const views = Boolean(item.is_video) && item.views !== null && item.views !== undefined;
    const value = views ? item.views : item.likes;
    const known = value !== null && value !== undefined;
    tile.querySelector('[data-mba-post-icon="views"]').hidden = !(views && known);
    tile.querySelector('[data-mba-post-icon="likes"]').hidden = views || !known;
    tile.querySelector('[data-mba-post-count]').textContent = known ? count(value) : '';
    return tile;
  };

  const showPosts = async (load) => {
    if (!el.postsStep || !el.postsGrid) return;
    postsController?.abort();
    lockPosts(false);
    el.postsGrid.replaceChildren();
    if (!load || !checker) return;
    const controller = new AbortController();
    postsController = controller;
    const handle = state.handle;
    el.postsGrid.setAttribute('aria-busy', 'true');
    el.postsGrid.replaceChildren(...Array.from({ length: 6 }, () => {
      const placeholder = document.createElement('li');
      placeholder.className = 'mba-posts__placeholder';
      return placeholder;
    }));
    const result = await checker.posts(platform, handle, { signal: controller.signal });
    if (controller.signal.aborted || handle !== state.handle) return;
    el.postsGrid.removeAttribute('aria-busy');
    const items = result.status === 'ok' ? result.items.slice(0, 12) : [];
    el.postsGrid.replaceChildren(...items.map((item, index) => postTile(item, index, items.length)).filter(Boolean));
  };

  // How many posts are currently chosen. Nothing at all stays blank rather than "0 selected": an
  // empty grid does not need a number to say it is empty.
  const showPostCount = () => {
    if (!el.postsCount) return;
    const total = state.posts.length;
    el.postsCount.textContent = total === 0 ? '' : (total === 1 ? S['post.chosen_one'] || '' : format(S['post.chosen_many'], { count: count(total) }));
  };

  // Several posts may be ticked at once: each becomes its own cart line, so the order carries one
  // link per video instead of a quantity against a single link.
  const tickedPosts = () => [...form.querySelectorAll('[data-mba-post-radio]:checked')].map((box) => box.value).filter(Boolean);

  const choosePost = (raw, { fromLink = false, showError = false } = {}) => {
    const text = String(raw ?? '').trim();
    let message = '';
    if (!fromLink || !text) {
      state.posts = tickedPosts();
      if (el.postLink && !fromLink) el.postLink.value = '';
    } else {
      // A pasted link is a target the grid does not offer, so it replaces the selection rather than
      // adding to it: there would be no tile to untick it with.
      const result = shell ? shell.validateTarget(text, 'post', platform) : { ok: /^https:\/\/\S+$/i.test(text), value: text };
      state.posts = result.ok ? [result.value] : [];
      if (result.ok) form.querySelectorAll('[data-mba-post-radio]:checked').forEach((box) => { box.checked = false; });
      if (!result.ok && showError) message = S['post.invalid'] || '';
    }
    if (el.postError) el.postError.textContent = message;
    el.postLink?.setAttribute('aria-invalid', String(Boolean(message)));
    showPostCount();
    refreshPrice();
    evaluate();
  };

  const applyResult = (result) => {
    const status = STATES.includes(result?.status) ? result.status : 'unavailable';
    // The check never closes the suggestions, not even when it finds an account: a prefix of the
    // handle someone is typing is often a real account too (@realthe exists while they aim for
    // @realtheoecom), so closing on a hit would snatch the list away mid-word. It closes when the
    // customer acts — picks one, presses Escape, clicks away or empties the field.
    state.status = status;
    state.profile = status === 'public' ? result : null;
    renderState(status, result);
    const posts = postStepMode(status);
    if (api && target === 'post' && !posts.locked) showPosts(posts.load);
    else hidePosts();
    updateProjection();
    evaluate();
  };

  const resetAccount = (clear) => {
    state.controller?.abort();
    Object.assign(state, { status: null, profile: null, handle: '' });
    if (clear && el.account) el.account.value = '';
    el.output?.replaceChildren();
    el.account?.removeAttribute('aria-invalid');
    hidePosts();
    evaluate();
    if (clear) el.account?.focus();
  };

  // Synchronous format check only (no network): used both to show "invalid" immediately on every
  // keystroke and, inside checkAccount(), before the (debounced) relay call.
  const evaluateAccountFormat = (raw) => {
    const handle = checks ? checks.parseHandle(platform, raw) : raw.replace(/^@/, '').toLowerCase();
    const valid = Boolean(handle) && (checks ? checks.isValidHandle(platform, handle) : /^[a-z0-9._]{1,30}$/.test(handle));
    return { handle, valid };
  };

  const checkAccount = async ({ fresh = false } = {}) => {
    if (!el.account) return null;
    await ready;
    const raw = el.account.value.trim();
    if (!raw) {
      resetAccount(false);
      return null;
    }
    const { handle, valid } = evaluateAccountFormat(raw);
    if (!valid) {
      state.controller?.abort();
      Object.assign(state, { status: 'invalid', profile: null, handle: '' });
      hidePosts();
      renderState('invalid');
      evaluate();
      return 'invalid';
    }
    // Minor: a blur (or any other trigger) for the same handle that is already checking, or already
    // resolved, must not restart the relay call — only a genuinely new handle or an explicit fresh=true do.
    if (!fresh && handle === state.handle && state.status) return state.status;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    Object.assign(state, { status: 'checking', profile: null, handle });
    hidePosts();
    renderState('checking');
    evaluate();
    const result = checker
      ? await checker.profile(platform, handle, { fresh, signal: controller.signal })
      : { status: 'unavailable', platform, username: handle };
    if (isStaleCheck({ aborted: controller.signal.aborted, requestedHandle: handle, currentHandle: state.handle })) return null;
    applyResult(result);
    return state.status;
  };

  const checkLink = (showError) => {
    if (!el.link) return;
    const raw = el.link.value;
    const linkType = target === 'post' ? 'post' : 'profile';
    const result = shell ? shell.validateTarget(raw, linkType, platform) : { ok: raw.trim().length > 2, value: raw.trim() };
    state.linkOk = result.ok;
    state.link = result.ok ? result.value : '';
    const message = !result.ok && showError && raw.trim() ? S[linkType === 'post' ? 'post.invalid' : 'account.invalid'] || '' : '';
    if (el.linkError) el.linkError.textContent = message;
    if (el.linkHint) el.linkHint.hidden = !result.ok;
    el.link.setAttribute('aria-invalid', String(Boolean(message)));
    evaluate();
  };

  const focusTarget = () => {
    const needPost = api && target === 'post' && (state.status === 'public' || state.status === 'unavailable') && !state.posts.length;
    const step = needPost ? el.postsStep : root.querySelector('[data-mba-step="account"], [data-mba-step="link"]');
    step?.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    const field = needPost ? step.querySelector('[data-mba-post-radio]') || el.postLink : el.account || el.link;
    field?.focus({ preventScroll: true });
  };

  const purchase = async (mode) => {
    if (state.busy) return;
    await ready;
    if (!evaluate()) {
      focusTarget();
      return;
    }
    const button = mode === 'now' ? el.now : el.add;
    state.busy = true;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    if (el.error) el.error.textContent = '';
    evaluate();
    let added = false;
    try {
      if (api && state.status === 'public' && checker) {
        // §7.4 / C2: re-check verified accounts ("Acheter maintenant" bypasses the 15 min cache), guarded
        // against staleness — the account field may have moved on to a different handle while this awaits.
        const handle = state.handle;
        state.controller?.abort();
        const controller = new AbortController();
        state.controller = controller;
        const result = await checker.profile(platform, handle, { fresh: mode === 'now', signal: controller.signal });
        if (isStaleCheck({ aborted: controller.signal.aborted, requestedHandle: handle, currentHandle: state.handle })) return;
        if (result.status !== 'public' && result.status !== 'unavailable') {
          applyResult(result);
          focusTarget();
          return;
        }
        // The pack/plan selection may also have changed while the re-check was in flight: re-evaluate.
        if (!evaluate()) {
          focusTarget();
          return;
        }
      }
      if (mode === 'now') {
        // Comme l'ancien theme : « Acheter maintenant » achete cet article seul. Sans ce vidage,
        // chaque tentative dont la redirection echoue laisse un article de plus dans le panier,
        // et le client retrouve plus tard un panier qu'il n'a pas voulu.
        try {
          await fetch(`${d.cartUrl}/clear.js`, { method: 'POST' });
        } catch {
          /* panier laisse tel quel : mieux vaut un doublon qu'un achat bloque */
        }
      }
      const items = lineItems();
      if (!items.length) return;
      const response = await fetch(`${d.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        let description = '';
        try {
          description = (await response.json())?.description || '';
        } catch {
          /* error body was not JSON */
        }
        if (el.error) el.error.textContent = description || S['buy.error'] || '';
        return;
      }
      added = true;
      // Minor: a failed /cart.js refresh must not swallow a successful add — itemCount stays null (no
      // event) but the drawer still opens / the cart page still loads below.
      let itemCount = null;
      try {
        const cart = await (await fetch(`${d.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
        itemCount = cart.item_count;
      } catch {
        itemCount = null;
      }
      if (itemCount !== null) document.dispatchEvent(new CustomEvent('mba:cart:updated', { detail: { itemCount } }));
      if (mode === 'now') {
        // Checkout MyBoost (Slash) allume : on y va directement, sans passer par le tiroir ni TagadaPay.
        if (window.mbSlashCheckout?.active) await window.mbSlashCheckout.start({ errorBox: el.error });
        else await clickCheckoutLink(readDiscountCode());
        return;
      }
      const drawer = document.getElementById('MinimogCartDrawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
        try {
          await drawer.onCartDrawerUpdate();
        } catch {
          /* best-effort refresh; the drawer still opens below */
        }
        if (typeof drawer.open === 'function') drawer.open();
      } else {
        window.location.href = d.cartUrl;
      }
    } catch {
      if (added && mode === 'now' && window.mbSlashCheckout?.active) await window.mbSlashCheckout.start({ errorBox: el.error });
      else if (added && mode === 'now') await clickCheckoutLink(readDiscountCode());
      else if (!added && el.error) el.error.textContent = S['buy.error'] || '';
    } finally {
      state.busy = false;
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
      evaluate();
    }
  };

  // Account suggestions. A shortcut for the step customers most often get wrong, never a verdict:
  // picking one runs the same relay check as typing it by hand.
  const suggest = { items: [], active: -1, controller: null, query: '' };

  const closeSuggest = () => {
    suggest.controller?.abort();
    Object.assign(suggest, { items: [], active: -1, controller: null });
    el.suggestList?.replaceChildren();
    if (el.suggest) el.suggest.hidden = true;
    el.account?.setAttribute('aria-expanded', 'false');
    el.account?.removeAttribute('aria-activedescendant');
  };

  const moveSuggest = (step) => {
    const total = suggest.items.length;
    if (!total) return;
    suggest.active = suggest.active < 0 && step < 0 ? total - 1 : (suggest.active + step + total) % total;
    [...(el.suggestList?.children ?? [])].forEach((row, index) => {
      const on = index === suggest.active;
      row.setAttribute('aria-selected', String(on));
      row.classList.toggle('is-active', on);
      if (on) {
        row.scrollIntoView({ block: 'nearest' });
        el.account?.setAttribute('aria-activedescendant', row.id);
      }
    });
  };

  const pickSuggest = (username) => {
    if (!el.account || !username) return;
    closeSuggest();
    suggestLater.cancel();
    accountLater.cancel();
    el.account.value = username;
    el.account.focus();
    checkAccount();
  };

  const suggestRow = (item, index) => {
    const row = el.suggestTemplate?.content.cloneNode(true).firstElementChild;
    if (!row) return null;
    row.id = `${el.account?.id || 'MbaSuggest'}-opt-${index}`;
    row.querySelector('[data-mba-suggest-handle]').textContent = `@${item.username}`;
    const badge = row.querySelector('[data-mba-suggest-verified]');
    if (badge) badge.hidden = !item.verified;
    // TikTok gives a number, Instagram a label it formatted itself.
    const followers = item.followers === null ? item.followers_label : format(S['account.followers'], { count: count(item.followers) });
    const meta = row.querySelector('[data-mba-suggest-meta]');
    if (meta) meta.textContent = [item.display_name, followers].filter(Boolean).join(' · ');
    const avatar = row.querySelector('[data-mba-suggest-avatar]');
    const src = resolveMediaUrl(item.avatar, relay);
    if (avatar && src) {
      avatar.src = src;
      avatar.hidden = false;
    }
    row.querySelector('[data-mba-suggest-pick]')?.addEventListener('click', () => pickSuggest(item.username));
    return row;
  };

  const runSuggest = async () => {
    if (!el.suggest || !el.account) return;
    await ready;
    if (typeof checker?.search !== 'function') return;
    const raw = el.account.value.trim();
    const term = (checks ? checks.parseHandle(platform, raw) : raw.replace(/^@/, '').toLowerCase()) || '';
    // A pasted profile URL is already unambiguous; a term the relay would refuse is not worth a call.
    if (raw.includes('/') || term.length < (checks?.MIN_SEARCH_LENGTH ?? 3)) return closeSuggest();
    // Une liste qui ne prolonge plus la saisie induit en erreur et masque le vrai message d'echec.
    if (suggest.query && term.indexOf(suggest.query) !== 0 && suggest.query.indexOf(term) !== 0) closeSuggest();
    suggest.controller?.abort();
    const controller = new AbortController();
    suggest.controller = controller;
    suggest.query = term;
    let result = await checker.search(platform, term, { signal: controller.signal });
    // La recherche d'Instagram echoue sur certains prefixes sans logique apparente : « real »
    // repond, « realtheo » non. Un repli sur un prefixe court rattrape ces trous. UN SEUL essai,
    // volontairement : chaque recherche appelle le relais RapidAPI, et en multiplier le nombre a
    // chaque frappe epuiserait le quota — la verification tomberait alors pour tout le monde.
    const courtPrefixe = Math.max(checks?.MIN_SEARCH_LENGTH ?? 3, 4);
    if ((result.status !== 'ok' || !result.items.length) && term.length > courtPrefixe) {
      const repli = await checker.search(platform, term.slice(0, courtPrefixe), { signal: controller.signal });
      if (controller.signal.aborted || suggest.query !== term) return;
      if (repli.status === 'ok' && repli.items.length) result = repli;
    }
    // The customer kept typing: this answer is for an older query.
    if (controller.signal.aborted || suggest.query !== term) return;
    // Instagram's search fails on some prefixes and answers the next one fine (@realtheo errors,
    // @realtheoec works), so a failure keeps whatever is already on screen instead of making the list
    // vanish and come back letter by letter. Only a successful empty answer really means "no match".
    if (result.status !== 'ok') return;
    if (!result.items.length || !el.suggestList) return closeSuggest();
    const { items } = result;
    Object.assign(suggest, { items, active: -1 });
    el.suggestList.replaceChildren(...items.map(suggestRow).filter(Boolean));
    el.suggest.hidden = false;
    el.account.setAttribute('aria-expanded', 'true');
    // La verification repond presque toujours avant la recherche : sans cela « Aucun compte »
    // resterait affiche en rouge au-dessus de la liste de comptes qu'on vient de proposer.
    if (state.status === 'not_found') {
      el.output?.replaceChildren();
      el.account.removeAttribute('aria-invalid');
    }
  };

  document.addEventListener('click', (event) => {
    if (el.suggest && !el.suggest.hidden && !el.suggest.contains(event.target) && event.target !== el.account) closeSuggest();
  });

  const accountLater = debounce(() => checkAccount(), 500);
  // Short on purpose: the list should keep up with the typing, not arrive after it.
  const suggestLater = debounce(() => runSuggest(), 150);
  const linkLater = debounce(() => checkLink(true), 500);

  el.account?.addEventListener('input', () => {
    const raw = el.account.value.trim();
    if (!raw) {
      accountLater.cancel();
      suggestLater.cancel();
      closeSuggest();
      resetAccount(false);
      return;
    }
    // One steady loading panel for the whole time the customer types. It used to be emptied and
    // rebuilt on every keystroke (and an invalid format rendered instantly), so it blinked open and
    // shut letter by letter. Every resolved state — invalid included — now waits for the debounce.
    if (state.status !== 'checking') {
      state.controller?.abort();
      Object.assign(state, { status: 'checking', profile: null, handle: '' });
      hidePosts();
      renderState('checking');
      evaluate();
    }
    accountLater();
    suggestLater();
  });
  el.account?.addEventListener('blur', () => {
    accountLater.cancel();
    if (el.account.value.trim()) checkAccount();
  });
  el.account?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeSuggest();
      return;
    }
    if (!suggest.items.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveSuggest(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter' && suggest.active >= 0) {
      event.preventDefault();
      pickSuggest(suggest.items[suggest.active].username);
    }
  });
  el.link?.addEventListener('input', () => {
    checkLink(false);
    linkLater();
  });
  el.link?.addEventListener('blur', () => {
    linkLater.cancel();
    checkLink(true);
  });
  el.postLink?.addEventListener('input', () => choosePost(el.postLink.value, { fromLink: true }));
  el.postLink?.addEventListener('change', () => choosePost(el.postLink.value, { fromLink: true, showError: true }));

  form.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-mba-post-radio]')) {
      choosePost(input.value);
      return;
    }
    if (input.matches('input[name="id"], input[name="selling_plan"]')) {
      if (input.name === 'id') keepSelectedPack();
      refreshPrice();
      evaluate();
    }
  });

  // Le navigateur refuse d'envoyer le formulaire tant qu'un champ obligatoire manque — la case de
  // consentement de l'abonnement, en pratique — mais le clic sur le bouton est intercepte en amont,
  // donc aucun message n'apparait et le client croit le bouton casse. L'evenement « invalid » est
  // emis par le navigateur sur le champ fautif au moment meme ou il bloque l'envoi : c'est le seul
  // signal fiable ici. Il ne remonte pas, d'ou l'ecoute en phase de capture.
  form.addEventListener('invalid', (event) => {
    const field = event.target;
    if (!field || typeof field.validationMessage !== 'string') return;
    if (el.error) el.error.textContent = field.validationMessage;
    try {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch {
      /* navigateurs sans options de scrollIntoView */
    }
  }, true);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const action = submitAction({
      submitter: event.submitter,
      active: document.activeElement,
      fields: { account: el.account, link: el.link, postLink: el.postLink },
    });
    if (action === 'buy-now' || action === 'add') {
      // A purchase triggered by the pressed button wins outright: a debounced check still in flight
      // must not resolve afterwards and stomp on state the purchase already moved past.
      accountLater.cancel();
      linkLater.cancel();
      purchase(action === 'buy-now' ? 'now' : 'add');
    } else if (action === 'check-account') {
      accountLater.cancel();
      checkAccount();
    } else if (action === 'check-link') {
      linkLater.cancel();
      checkLink(true);
    } else if (action === 'choose-post') {
      choosePost(el.postLink.value, { fromLink: true, showError: true });
    }
  });

  // Minor: move focus into the status region when « Revérifier »/« Réessayer » is pressed, so a
  // keyboard/screen-reader user has a stable landing spot. This must happen when the check STARTS, not
  // once it resolves: renderState('checking') replaces el.output's children synchronously (checkAccount's
  // very next tick), which removes the clicked button itself from the DOM. Waiting for the network
  // response before focusing would leave focus nowhere in the meantime. el.output itself is never
  // replaced (only its children are), so it is safe to focus before the "checking" markup even exists.
  const focusAccountOutput = () => {
    if (!el.output) return;
    el.output.setAttribute('tabindex', '-1');
    el.output.focus({ preventScroll: true });
  };

  // Collapsing must never hide the pack the customer just picked: the chosen card stays on show
  // even when it came from the folded part of the list.
  const keepSelectedPack = () => {
    const grid = form.querySelector('[data-mba-packs]');
    if (!grid) return;
    grid.querySelectorAll('.is-kept').forEach((label) => label.classList.remove('is-kept'));
    grid.querySelector('input[name="id"]:checked')?.closest('.mba-pack--more')?.classList.add('is-kept');
  };

  const togglePacks = (button, expand) => {
    const fieldset = button.closest('[data-mba-step="packs"]');
    fieldset?.classList.toggle('is-expanded', expand);
    button.setAttribute('aria-expanded', String(expand));
    const label = button.querySelector('[data-mba-packs-more-label]');
    if (label) label.textContent = expand ? S['packs.less'] : S['packs.more'];
    if (expand) fieldset?.querySelector('.mba-pack--more input:not(:disabled)')?.focus();
    else keepSelectedPack();
  };

  root.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button || !root.contains(button)) return;
    if (button.matches('[data-mba-recheck], [data-mba-retry]')) {
      focusAccountOutput();
      checkAccount({ fresh: true });
    } else if (button.matches('[data-mba-other]')) {
      resetAccount(true);
    } else if (button.matches('[data-mba-packs-more]')) {
      togglePacks(button, button.getAttribute('aria-expanded') !== 'true');
    } else if (button.matches('[data-mba-sticky-action]')) {
      const mode = button.dataset.mbaMode;
      if (mode === 'add') purchase('add');
      else if (mode === 'recheck') checkAccount({ fresh: true }).then(focusTarget);
      else focusTarget();
    }
  });

  refreshPrice();
  evaluate();
  initMedia(root);
  initSticky(root, el.buy, el.sticky);
  ready.then(() => {
    refreshPrice();
    if (el.link?.value.trim()) checkLink(false);
    if (el.account?.value.trim()) checkAccount();
    evaluate();
  });
}

function boot(scope = document) {
  scope.querySelectorAll?.('[data-mba-product]').forEach((root) => initProduct(root));
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
}
